import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { useState } from "react";
import {
  convertToCurrencyFormat,
  stockUnitOfMeasureOptions,
  isReadOnlyUser,
} from "../../../shared/utility";
import NumberInputText from "../../controls/inputs/NumberInputText";
import InlineSelectMenu from "../../controls/inputs/InlineSelectMenu";
import { useQueuedRowEdits } from "../../../hooks/useQueuedRowEdits";
import OrderSubtable1 from "./subtables/OrderSubtable1";
import TableLoadingSkeleton from "../../shared/TableLoadingSkeleton";
import TraceableLotNumber, {
  getInventoryLotTraceType,
} from "../../controls/TraceableLotNumber";

export default function OrderProductsTable(props) {
  const {
    loading,
    values,
    onItemEditComplete,
    rowDetails,
    fetchRowDetails,
    loadingRows,
    fetchSubRowDetails,
    subRowDetails,
    subLoadingRows,
    selectedItems,
    setSelectedItems,
    closed,
  } = props;

  const [expandedRows, setExpandedRows] = useState(null);
  const isReadOnly = isReadOnlyUser();

  const allowExpansion = (rowData) => {
    return true;
  };

  const rowExpansionTemplate = (data) => {
    return (
      <OrderSubtable1
        data={data}
        loadingRows={loadingRows}
        rowDetails={rowDetails}
        fetchSubRowDetails={fetchSubRowDetails}
        subRowDetails={subRowDetails}
        subLoadingRows={subLoadingRows}
        selectedItems={selectedItems}
        setSelectedItems={setSelectedItems}
      />
    );
  };

  const {
    rows: items,
    getFieldSaveStatus,
    saveField,
  } = useQueuedRowEdits({
    rows: values.items ?? [],
    keyPrefix: "shipment",
    onSave: (item, rowIndex) => onItemEditComplete(item, rowIndex),
  });

  const saveItemField = (item, rowIndex, field, value) =>
    saveField(item, field, value, rowIndex);

  const productDetailsBody = (rowData) => {
    return (
      <p>
        <span className="text-gray-900 font-semibold">
          {rowData.product_name}
        </span>
        <br />
        <span className="text-gray-700">PLU:{rowData.plu}</span>
      </p>
    );
  };

  const unitCostBody = (rowData, { rowIndex }) => {
    return (
      <NumberInputText
        value={rowData.unit_cost}
        aria-label={`Unit cost for ${rowData.product_name}`}
        inputClassName="editable-table-input"
        className="editable-table-number"
        disabled={isReadOnly || closed}
        saveStatus={getFieldSaveStatus(rowData, "unit_cost")}
        onCommit={(unitCost) =>
          saveItemField(rowData, rowIndex, "unit_cost", unitCost)
        }
        mode="currency"
        currency="USD"
        locale="en-US"
        minFractionDigits={2}
        maxFractionDigits={2}
        min={0}
      />
    );
  };

  const totalCostBody = (rowData) => {
    return <p>{convertToCurrencyFormat(rowData.total_cost)}</p>;
  };

  const totalCartonsParent = (rowData) => {
    return (
      <div>
        <p>{rowData.total_cartons} cartons</p>
      </div>
    );
  };

  const totalShippedWeightParent = (rowData) => {
    return (
      <div>
        <p>{rowData.total_in_kg}kg</p>
        <p>{rowData.total_in_lbs}lbs</p>
        <p>{rowData.total_in_pieces} pieces</p>
      </div>
    );
  };

  const unitTypeBody = (rowData, { rowIndex }) => {
    return (
      <InlineSelectMenu
        name={`unit_type_${rowData.id}`}
        ariaLabel={`Unit type for ${rowData.product_name}`}
        options={stockUnitOfMeasureOptions}
        value={stockUnitOfMeasureOptions.find(
          (option) => option.value === rowData.unit_type,
        )}
        disabled={isReadOnly || closed}
        saveStatus={getFieldSaveStatus(rowData, "unit_type")}
        onCommit={(option) =>
          saveItemField(rowData, rowIndex, "unit_type", option.value)
        }
        menuPortalTarget={document.body}
      />
    );
  };

  const onRowExpand = (event) => {
    const rowData = event.data;
    if (!rowDetails[rowData.unique_id]) {
      fetchRowDetails(rowData);
    }
  };
  return (
    <div className="w-full overflow-x-auto">
      <div className="min-w-[900px] py-2">
          {loading ? (
            <TableLoadingSkeleton rows={5} columns={4} />
          ) : (
            <div className="overflow-hidden">
              <DataTable
                value={items}
                expandedRows={expandedRows}
                onRowToggle={(e) => {
                  setExpandedRows(e.data);
                }}
                onRowExpand={onRowExpand}
                rowExpansionTemplate={rowExpansionTemplate}
                dataKey="id"
                tableClassName="min-w-full bg-white divide-y divide-gray-300 create-order-inventory-table-parent"
                cellClassName="p-2 bg-white border-1-bottom-gray text-sm"
              >
                <Column header="Product" body={productDetailsBody} />
                <Column
                  header="Lot Number"
                  body={(item) => (
                    <TraceableLotNumber
                      lotNumber={item.lot_number}
                      traceType={getInventoryLotTraceType(item)}
                    />
                  )}
                />

                <Column
                  header="Shipped Cartons"
                  body={totalCartonsParent}
                  style={{ textAlign: "right" }}
                />

                <Column
                  header="Shipped amount"
                  body={totalShippedWeightParent}
                  style={{ textAlign: "right" }}
                />

                <Column
                  header="Unit Cost"
                  field="unit_cost"
                  style={{ textAlign: "right" }}
                  body={unitCostBody}
                />

                <Column
                  header="Total Price"
                  field="total_cost"
                  style={{ textAlign: "right" }}
                  body={totalCostBody}
                />

                <Column
                  header="Unit Type"
                  field="unit_type"
                  headerStyle={{ minWidth: "8rem" }}
                  body={unitTypeBody}
                />
                <Column expander={allowExpansion} style={{ width: "6%" }} />
              </DataTable>
            </div>
          )}
          <div className="w-full text-right mt-4">
            <p className="font-bold">
              Subtotal: {convertToCurrencyFormat(values.subtotal_dollar_amount)}
            </p>
            <p className="font-bold">
              Taxes: {convertToCurrencyFormat(values.tax_amount)}
            </p>
            <p className="font-bold">
              Total: {values.currency?.label || ""}{" "}
              {convertToCurrencyFormat(values.total_dollar_amount)}
            </p>
          </div>
      </div>
    </div>
  );
}
